<script lang="ts" setup>
import { ref, computed } from 'vue'
import { Input } from 'ant-design-vue'
import TextView from '@/ui/common/text-view/TextView.vue'
import { useDebounceFn } from '@vueuse/core'
import { useRouter } from 'vue-router'
import lupa from '@/assets/icons/search/lupa.svg'
import { ThreadsApi } from '@/api/modules/threads/api'
import type { IThreadItem } from '@/api/modules/threads/types'
import { Routes } from '@/config/router/routes'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const router = useRouter()

interface Props {
  onClose?: () => void
}

const props = defineProps<Props>()
const searchInputRef = ref()
const searchQuery = ref('')
const searchResults = ref<IThreadItem[]>([])
const isLoading = ref(false)
const isDropdownOpen = ref(false)
const hoveredIndex = ref(-1)

const searchThreadsApi = async (query: string): Promise<IThreadItem[]> => {
  try {
    const response = await ThreadsApi.searchThreads(query, 1, 20)
    return response.data.results
  } catch (error) {
    console.error('Search threads error:', error)
    return []
  }
}

const debouncedSearch = useDebounceFn(async (query: string) => {
  if (!query.trim()) {
    closeSearch()
    return
  }

  isLoading.value = true
  try {
    const results = await searchThreadsApi(query)
    searchResults.value = results
    isDropdownOpen.value = true
  } catch (error) {
    searchResults.value = []
    isDropdownOpen.value = true
  } finally {
    isLoading.value = false
  }
}, 300)

const handleInputChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  searchQuery.value = target.value
  debouncedSearch(target.value)
}

const handleSelectItem = (item: IThreadItem) => {
  router.push({
    name: Routes.ChatDetail,
    params: { id: item.thread_id },
  })

  closeSearch()
}

const closeSearch = () => {
  props.onClose?.()
  isDropdownOpen.value = false
  searchQuery.value = ''
  searchResults.value = []
  hoveredIndex.value = -1
}

const handleKeyDown = (e: KeyboardEvent) => {
  // if (!isDropdownOpen.value) return

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      hoveredIndex.value = Math.min(hoveredIndex.value + 1, searchResults.value.length - 1)
      break
    case 'ArrowUp':
      e.preventDefault()
      hoveredIndex.value = Math.max(hoveredIndex.value - 1, -1)
      break
    case 'Enter':
      e.preventDefault()
      if (hoveredIndex.value >= 0 && searchResults.value[hoveredIndex.value]) {
        handleSelectItem(searchResults.value[hoveredIndex.value])
      }
      break
    case 'Escape':
      closeSearch()
      break
  }
}

const groupedResults = computed(() => {
  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)

  const threeDaysAgo = new Date(now)
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3)

  const sevenDaysAgo = new Date(now)
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

  const groups: { [key: string]: IThreadItem[] } = {
    yesterday: [],
    treedays: [],
    week: [],
    before: [],
  }

  searchResults.value.forEach((item) => {
    const itemDate = new Date(item.modified_at)

    if (itemDate >= yesterday) {
      groups['yesterday'].push(item)
    } else if (itemDate >= threeDaysAgo) {
      groups['treedays'].push(item)
    } else if (itemDate >= sevenDaysAgo) {
      groups['week'].push(item)
    } else {
      groups['before'].push(item)
    }
  })

  return Object.entries(groups)
    .filter(([_, items]) => items.length > 0)
    .map(([groupName, items]) => ({ groupName, items: items.slice(0, 5) }))
})

const hasResults = computed(() => searchResults.value.length > 0)

onMounted(() => {
  if (searchInputRef.value) {
    searchInputRef.value.focus()
  }
})
</script>

<template>
  <div class="search-container" @keydown="handleKeyDown">
    <Input
      ref="searchInputRef"
      v-model:value="searchQuery"
      :placeholder="t('ui.search')"
      size="large"
      class="search-input"
      :loading="isLoading"
      @input="handleInputChange"
      @focus="isDropdownOpen = searchResults.length > 0"
    >
      <template #suffix>
        <img :src="lupa" alt="Лупа" />
      </template>
    </Input>

    <div v-if="isDropdownOpen" class="search-dropdown">
      <!-- Группированные результаты -->
      <div v-for="group in groupedResults" :key="group.groupName" class="search-dropdown-group">
        <TextView variant="roboto_11_regular" class="search-dropdown-group__title">
          {{ t(`ui.search-${group.groupName}`) }}
        </TextView>

        <div
          v-for="(item, index) in group.items"
          :key="item.thread_id"
          :class="[
            'search-dropdown-item',
            { 'search-dropdown-item_hovered': hoveredIndex === index },
          ]"
          @click="handleSelectItem(item)"
          @mouseenter="hoveredIndex = index"
          @mouseleave="hoveredIndex = -1"
        >
          <TextView variant="roboto_13_regular" class="search-dropdown-item__message">
            {{ item.matched_message }}
          </TextView>
        </div>
      </div>

      <div v-if="!hasResults && !isLoading" class="search-dropdown-empty">
        <TextView variant="roboto_13_regular" class="search-dropdown-empty__text">
          {{ t('ui.search-dropdown-text') }}
        </TextView>
        <TextView variant="roboto_11_regular" class="search-dropdown-empty__hint">
          {{ t('ui.search-dropdown-hint') }}
        </TextView>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/media' as media;

.search-container {
  position: relative;
  width: 700px;

  @include media.max(media.$tablet) {
    width: 400px;
  }

  @include media.max(media.$mobile) {
    width: 300px;
  }
}

.search-input {
  width: 100%;
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 4px;
}

.search-dropdown-group {
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }

  &__title {
    padding: 4px 12px;
    color: #8c8c8c;
    text-transform: lowercase;
    font-size: 11px;
  }
}

.search-dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  &_hovered {
    background-color: #e6f7ff;
  }

  &__message {
    color: #262626;
    line-height: 1.4;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}

.search-dropdown-empty {
  padding: 24px 12px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__text {
    color: #8c8c8c;
  }

  &__hint {
    color: #bfbfbf;
  }
}
</style>
